import { useMemo } from "react";
import { Formik, validateYupSchema, yupToFormErrors } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DateTime } from "luxon";
import { FunnelButton } from "../../FunnelButton";
import { FunnelBackButton } from "../../FunnelBackButton";
import { FormFieldRenderer, QuestionConfig } from "../../FormFieldRenderer";
import { logger } from "@portal/app/_common/lib/logger";
import type {
  CustomerFieldMapping,
  CustomerMappableField,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import type { EligibilityStepProps } from "./types";
import { transformValue, buildMutationVariables } from "./utils";
import { populateCustomerDataRef } from "./preCollectedFieldProcessor";
import { useFormFunnelTracking } from "../../hooks/useFormFunnelTracking";
import { parseGradYear } from "@portal/app/_common/lib/gradYear";
import { useHostedStepAction } from "./useHostedStepAction";

/** CMS validation config shape, matching PayloadFormQuestion.validation */
interface ValidationConfig {
  required?: boolean;
  pattern?: string;
  customError?: string;
  minLength?: number;
  maxLength?: number;
}

interface HostedDynamicStepProps extends EligibilityStepProps {
  questionConfig: QuestionConfig;
  customerFieldMapping: Required<Pick<CustomerFieldMapping, "customerField" | "valueTransform">>;
  /** CMS validation config for pattern/customError/minLength/maxLength */
  validationConfig?: ValidationConfig;
  /** Matched program's allowed graduation-year range, applied to graduatedHighSchoolIn */
  gradYearMin?: number | null;
  gradYearMax?: number | null;
}

function getInitialValueFromViewer(
  viewerCustomer: EligibilityStepProps["viewerCustomer"],
  customerField: CustomerMappableField
): string | DateTime {
  const dm = viewerCustomer?.detailsMap;
  switch (customerField) {
    // Boolean fields: radio options use string "true"/"false" values
    case "isUsCitizen": {
      const val = dm?.isUsCitizen;
      return val == null ? "" : String(val);
    }
    case "hasRNLicense": {
      const val = dm?.hasRNLicense;
      return val == null ? "" : String(val);
    }
    case "hasMilitaryAffiliation": {
      const val = dm?.hasMilitaryAffiliation;
      return val == null ? "" : String(val);
    }
    // String fields
    case "graduatedHighSchoolIn":
      return dm?.graduatedHighSchoolIn ?? "";
    case "dob": {
      const dob = dm?.dob ?? "";
      if (!dob) return "";
      const parsed = DateTime.fromISO(dob);
      return parsed.isValid ? parsed : dob;
    }
    case "educationLevel":
      return dm?.educationLevel ?? "";
    case "acceptedTermsVersion":
      return dm?.acceptedTermsVersion ?? "";
    // Personal info fields (name is on the user object, not detailsMap)
    case "firstName":
      return viewerCustomer?.user?.nameFirst ?? "";
    case "lastName":
      return viewerCustomer?.user?.nameLast ?? "";
    case "email":
      return dm?.email ?? "";
    case "phone":
      return dm?.phone ?? "";
    // Address fields
    case "address.zip":
      return dm?.address?.zip ?? "";
    case "address.street":
      return dm?.address?.street ?? "";
    case "address.city":
      return dm?.address?.city ?? "";
    case "address.state":
      return dm?.address?.state ?? "";
    // Career / questionnaire fields
    case "careerGoal":
      return dm?.careerGoal ?? "";
    case "careerChangeConcern":
      return dm?.careerChangeConcern ?? "";
    case "careerTimeline":
      return dm?.careerTimeline ?? "";
    case "careerAspectsRanking":
      return dm?.careerAspectsRanking ? JSON.stringify(dm.careerAspectsRanking) : "";
    case "careerAspectsDesired":
      return dm?.careerAspectsDesired ?? "";
    case "jobEnjoyment":
      return dm?.jobEnjoyment ?? "";
    case "jobDislike":
      return dm?.jobDislike ?? "";
    // Skipper / preference fields
    case "coachStyle":
      return dm?.coachStyle ?? "";
    case "degreeLevelInterest":
      return dm?.degreeLevelInterest ? JSON.stringify(dm.degreeLevelInterest) : "";
    case "furtherEducationReason":
      return dm?.furtherEducationReason ? JSON.stringify(dm.furtherEducationReason) : "";
    case "industryInterest":
      return dm?.industryInterest ?? "";
    case "mainFocus":
      return dm?.mainFocus ? JSON.stringify(dm.mainFocus) : "";
    case "subindustryInterest":
      return dm?.subindustryInterest ? JSON.stringify(dm.subindustryInterest) : "";
    case "timeframePreference":
      return dm?.timeframePreference ?? "";
    case "enrollLikelihood":
      return dm?.enrollLikelihood ?? "";
    case "programEnvironment":
      return dm?.programEnvironment ?? "";
    default: {
      const _exhaustive: never = customerField;
      return _exhaustive;
    }
  }
}

/** Build a Yup schema from the CMS validation config and customer field type */
function buildFieldSchema(
  label: string,
  validationConfig: ValidationConfig | undefined,
  customerField: CustomerMappableField,
  required: boolean,
  gradYearMin?: number | null,
  gradYearMax?: number | null
): Yup.AnySchema {
  // Graduation year needs a range check beyond the 4-digit pattern; the matched
  // program's bounds narrow the default 1900..current-year window when configured
  if (customerField === "graduatedHighSchoolIn") {
    const minYear = gradYearMin ?? 1900;
    const maxYear = gradYearMax ?? DateTime.now().year;
    return Yup.string()
      .required(`${label} is required`)
      .test("valid-year", `Must be between ${minYear} and ${maxYear}`, (val) => {
        if (!val) return false;
        const year = parseGradYear(val);
        return year != null && year >= minYear && year <= maxYear;
      });
  }

  // DOB fields store a Luxon DateTime object (via FormInputDateWithFormik), not a string
  if (customerField === "dob") {
    // When the program configures DOB as OPTIONAL, empty values are allowed.
    // Any non-empty value must still parse as a valid date so we don't submit
    // garbage to the backend.
    const dobSchema = Yup.mixed().test("is-date", "Invalid date", (value) => {
      if (!value) return !required;
      if (typeof value === "object" && value !== null && "isValid" in value) {
        return (value as DateTime).isValid;
      }
      if (typeof value === "string") {
        return DateTime.fromISO(value).isValid;
      }
      return false;
    });
    return required ? dobSchema.required(`${label} is required`) : dobSchema;
  }

  let schema = Yup.string();

  // Required validation (all other hosted steps are always required)
  schema = schema.required(`${label} is required`);

  // Pattern from CMS (e.g., "^\d{5}$" for ZIP, "^\d{4}$" for year)
  if (validationConfig?.pattern) {
    try {
      const regex = new RegExp(validationConfig.pattern);
      schema = schema.matches(regex, validationConfig.customError || "Invalid format");
    } catch (e) {
      logger.warn(
        `[HostedDynamicStep] Invalid regex pattern from CMS: "${validationConfig.pattern}", skipping pattern validation`
      );
    }
  }

  if (validationConfig?.minLength) {
    schema = schema.min(
      validationConfig.minLength,
      validationConfig.customError || `Must be at least ${validationConfig.minLength} characters`
    );
  }

  if (validationConfig?.maxLength) {
    schema = schema.max(
      validationConfig.maxLength,
      validationConfig.customError || `Must be at most ${validationConfig.maxLength} characters`
    );
  }

  return schema;
}

export const HostedDynamicStep = ({
  viewerCustomer,
  customerDataRef,
  updateCustomerDetails,
  refetchViewer,
  onNext,
  onBack,
  questionConfig,
  customerFieldMapping,
  validationConfig,
  gradYearMin,
  gradYearMax,
}: HostedDynamicStepProps) => {
  const { customerField, valueTransform } = customerFieldMapping;
  const { trackEvent } = useFormFunnelTracking();
  const fieldName = questionConfig.id;
  const action = useHostedStepAction(fieldName);
  const isRequired = questionConfig.required !== false;

  const initialValues = useMemo(
    () => ({ [fieldName]: getInitialValueFromViewer(viewerCustomer, customerField) }),
    [fieldName, viewerCustomer, customerField]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        [fieldName]: buildFieldSchema(
          questionConfig.label,
          validationConfig,
          customerField,
          isRequired,
          gradYearMin,
          gradYearMax
        ),
      }),
    [
      fieldName,
      questionConfig.label,
      validationConfig,
      customerField,
      isRequired,
      gradYearMin,
      gradYearMax,
    ]
  );

  // Seed errors synchronously so Continue reflects the initial answer's
  // validity from the first render, without a second mount validation.
  const initialErrors = useMemo(() => {
    try {
      validateYupSchema(initialValues, validationSchema, true);
      return {};
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        return yupToFormErrors<Record<string, string>>(error);
      }
      throw error;
    }
  }, [initialValues, validationSchema]);

  return (
    <Formik
      key={fieldName}
      initialValues={initialValues}
      initialErrors={initialErrors}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const generation = action.begin();
        if (generation === undefined) return;
        try {
          const fieldValue = values[fieldName] as unknown;
          // Date fields store a Luxon DateTime — convert to "yyyy-MM-dd" string for the backend
          const rawValue = DateTime.isDateTime(fieldValue)
            ? fieldValue.toFormat("yyyy-MM-dd")
            : (fieldValue as string);

          // When the field is optional and the user submits with no value, skip the
          // mutation entirely (backend validators reject empty strings for typed
          // fields like dob). Mirrors the Skip button's behavior.
          if (!isRequired && !rawValue) {
            if (!action.complete(generation)) return;
            trackEvent("Form Funnel - Hosted Step Completed", {
              stepType: "eligibility",
              field: customerField,
              skipped: true,
            });
            onNext();
            return;
          }

          const transformed = transformValue(rawValue, valueTransform);
          const mutationVars = buildMutationVariables(customerField, transformed);

          // Address fields write to customerDataRef for downstream ConfirmStep
          if (customerField.startsWith("address.")) {
            const addressKey = customerField.split(".")[1] as string;
            const currentAddress = customerDataRef.current.address;
            customerDataRef.current.address = {
              zip: currentAddress?.zip ?? "",
              ...currentAddress,
              [addressKey]: rawValue,
            };
          }

          // Populate known customer fields (dob, educationLevel) in the ref
          // so ConfirmStep has the data even before the mutation completes.
          populateCustomerDataRef(customerDataRef, customerField, rawValue);

          await updateCustomerDetails({ variables: { data: mutationVars } });
          await refetchViewer();
          if (!action.complete(generation)) return;
          trackEvent("Form Funnel - Hosted Step Completed", {
            stepType: "eligibility",
            field: customerField,
          });
          onNext();
        } catch (e) {
          logger.error(e);
        } finally {
          action.finish(generation);
        }
      }}
    >
      {({ handleSubmit, isValid, isValidating, isSubmitting }) => (
        <form onSubmit={handleSubmit} data-testid="hosted-dynamic-step">
          {onBack && (
            <FunnelBackButton appearance="text" onClick={onBack} sx={{ mb: 1 }}>
              Back
            </FunnelBackButton>
          )}
          <Box
            sx={{
              '& [data-testid^="funnel-field-"]': { alignItems: "stretch" },
              "& [data-funnel-question-label]": {
                alignSelf: "flex-start",
                color: "text.primary",
                fontSize: "1.5rem",
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
                textAlign: "left",
                mb: 3,
              },
              '& [data-testid^="funnel-field-"] > div': { alignItems: "stretch" },
            }}
          >
            <FormFieldRenderer question={questionConfig} selectAppearance="hosted" />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              mt: 3,
              width: "100%",
            }}
          >
            <FunnelButton
              type="submit"
              disabled={!isValid || isValidating || isSubmitting}
              sx={{ maxWidth: "none", minHeight: 56, fontSize: "1.1rem" }}
            >
              Continue
            </FunnelButton>
            {!isRequired && (
              <Button
                type="button"
                variant="text"
                onClick={() => {
                  const generation = action.begin();
                  if (generation === undefined || !action.complete(generation)) return;
                  trackEvent("Form Funnel - Hosted Step Completed", {
                    stepType: "eligibility",
                    field: customerField,
                    skipped: true,
                  });
                  onNext();
                }}
                disabled={isSubmitting}
                data-testid="hosted-dynamic-step-skip"
                sx={{ alignSelf: "center" }}
              >
                Skip
              </Button>
            )}
          </Box>
        </form>
      )}
    </Formik>
  );
};
